import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTruckStore, TRUCK_SIZES } from "@/stores/truck-store";
import { Truck, TruckIcon, Ship, HelpCircle, Box, PackageOpen } from "lucide-react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export function TruckSelectionModal() {
  const { showTruckModal, setTruckSize, setShowTruckModal, calculatorMode, setCalculatorMode } = useTruckStore();

  const handleTruckSelect = (truckSize: string) => {
    setTruckSize(truckSize);
  };

  const truckOptions = [
    {
      key: "24ft_box",
      icon: Truck,
      title: "24-foot Box Truck",
      capacity: "~27,648 sq inches capacity",
      dimensions: "96\" wide × 288\" deep",
    },
    {
      key: "53ft_semi",
      icon: TruckIcon,
      title: "53-foot Semi Trailer",
      capacity: "~64,872 sq inches capacity",
      dimensions: "102\" wide × 636\" deep",
    },
    {
      key: "40ft_container",
      icon: Ship,
      title: "40-foot High Cube Container",
      capacity: "~43,424 sq inches capacity",
      dimensions: "92\" wide × 472\" deep",
    },
    {
      key: "unknown",
      icon: HelpCircle,
      title: "I don't know",
      capacity: "No capacity tracking",
      dimensions: "Shop without constraints",
    },
  ];

  return (
    <Dialog open={showTruckModal} onOpenChange={setShowTruckModal}>
      <DialogContent className="max-w-2xl" data-testid="truck-selection-modal">
        <DialogHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Truck className="h-12 w-12 text-primary" />
          </div>
          <DialogTitle className="text-3xl font-bold mb-2">
            How much are you planning to buy?
          </DialogTitle>
          <DialogDescription className="text-base">
            Select your truck size to help us calculate loading capacity
          </DialogDescription>
        </DialogHeader>

        {/* Calculator Mode Toggle */}
        <div className="bg-secondary/30 p-4 rounded-lg border">
          <Label className="text-sm font-semibold mb-3 block">Calculator Mode</Label>
          <RadioGroup value={calculatorMode} onValueChange={(value: any) => setCalculatorMode(value)} className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2 cursor-pointer">
              <RadioGroupItem value="2d" id="mode-2d" data-testid="radio-mode-2d" />
              <Label htmlFor="mode-2d" className="cursor-pointer flex items-center gap-2">
                <Box className="h-4 w-4" />
                <div>
                  <div className="font-medium">2D Simple</div>
                  <div className="text-xs text-muted-foreground">Quick floor planning</div>
                </div>
              </Label>
            </div>
            <div className="flex items-center space-x-2 cursor-pointer">
              <RadioGroupItem value="3d" id="mode-3d" data-testid="radio-mode-3d" />
              <Label htmlFor="mode-3d" className="cursor-pointer flex items-center gap-2">
                <PackageOpen className="h-4 w-4" />
                <div>
                  <div className="font-medium">3D Advanced</div>
                  <div className="text-xs text-muted-foreground">Paxter optimization</div>
                </div>
              </Label>
            </div>
          </RadioGroup>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {truckOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <Button
                key={option.key}
                variant="outline"
                className="h-auto p-6 text-left flex-col items-start hover:border-primary hover:bg-secondary/50 transition-all group"
                onClick={() => handleTruckSelect(option.key)}
                data-testid={`truck-option-${option.key}`}
              >
                <IconComponent className="h-8 w-8 text-primary mb-3" />
                <h3 className="font-semibold text-lg mb-1 text-foreground">{option.title}</h3>
                <p className="text-sm text-muted-foreground mb-2">{option.capacity}</p>
                <p className="text-xs text-muted-foreground">{option.dimensions}</p>
              </Button>
            );
          })}
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-muted-foreground">
            You can change this later in cart settings
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
